import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Box,
  Typography
} from '@mui/material';
import { Save } from '@mui/icons-material';

const ProjectModal = ({ open, onClose, project, companies, onSave, loadingText = 'Saving project...' }) => {
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    conditions_of_acceptance: '',
    customer: '',
    planned_start_date: '',
    planned_end_date: '',
    company_ids: []
  });
  const [loading, setLoading] = useState(false);

  const convertToInputDate = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [day, month, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const convertToApiDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        number: project.number || '',
        conditions_of_acceptance: project.conditions_of_acceptance || '',
        customer: project.customer || '',
        planned_start_date: convertToInputDate(project.planned_start_date) || '',
        planned_end_date: convertToInputDate(project.planned_end_date) || '',
        company_ids: project.company_ids || []
      });
    } else {
      setFormData({
        name: '',
        number: '',
        conditions_of_acceptance: '',
        customer: '',
        planned_start_date: '',
        planned_end_date: '',
        company_ids: []
      });
    }
  }, [project, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        ...formData,
        planned_start_date: convertToApiDate(formData.planned_start_date),
        planned_end_date: convertToApiDate(formData.planned_end_date)
      };

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const selectedCompanies = companies.filter(company =>
    formData.company_ids.includes(company.id)
  );

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="lg" fullWidth>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            borderRadius: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}
          >
            <Save sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6" color="primary.main">
              {loadingText}
            </Typography>
          </Box>
          <style>
            {`
              @keyframes pulse {
                0% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.1); opacity: 0.7; }
                100% { transform: scale(1); opacity: 1; }
              }
            `}
          </style>
        </Box>
      )}
      <DialogTitle>
        {project ? 'Edit Project' : 'Add New Project'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Number"
                value={formData.number}
                onChange={(e) => handleChange('number', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Conditions of acceptance"
                value={formData.conditions_of_acceptance}
                onChange={(e) => handleChange('conditions_of_acceptance', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Customer"
                value={formData.customer}
                onChange={(e) => handleChange('customer', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Planned start date"
                type="date"
                value={formData.planned_start_date}
                onChange={(e) => handleChange('planned_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Planned end date"
                type="date"
                value={formData.planned_end_date}
                onChange={(e) => handleChange('planned_end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Autocomplete
              multiple
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedCompanies}
              onChange={(event, newValue) => {
                handleChange('company_ids', newValue.map(company => company.id));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Companies"
                  placeholder="Select companies"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="success" disabled={loading}>
          {project ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectModal;
